import React from 'react';
import { Textarea } from '@fluentui/react-components';
import { useDispatch } from 'react-redux';
import { GetDataroomItemInfo } from '../../redux/dataroomSlice';
import { AddBulkInformationRequests, GetDefaultQuestions } from '../../redux/questionBankSlice';
import NewProjectWizardCard from './NewProjectWizardCard';
import { irlCatalogForWorkstream, irlWhyText } from './irlRequestCatalog';
import { suggestIrl } from './projectSetupApi';
import DocumentContextBanner from './DocumentContextBanner';
import SetupSuggestionLoader from './SetupSuggestionLoader';
import styles from './css/index.module.css';
import checkboxCheck from './assets/checkbox-check.svg';

const nextKey = () => `irl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

function toRequestItem(entry, workstreamName) {
    const folder = entry.folder || entry.tag || 'General';
    const request = entry.request || entry.question || '';
    const high = Boolean(entry.high) || entry.priority === 'High' || entry.priorities === 'HIGH';
    return {
        key: nextKey(),
        folder,
        request,
        high,
        selected: entry.selected !== false && entry.recommended !== false,
        custom: Boolean(entry.custom),
        why: entry.reason || irlWhyText(folder, workstreamName),
        priorities: entry.priorities || (high ? 'HIGH' : 'NONE'),
    };
}

function buildRequestsFromSuggestion(requests = [], workstreamId, workstreamName) {
    return (requests || [])
        .filter(item => String(item.workstream_id) === String(workstreamId))
        .map(item =>
            toRequestItem(
                {
                    folder: item.folder_name || item.folder || 'General',
                    request: item.request,
                    high: item.priority === 'High',
                    selected: item.recommended !== false,
                    reason: item.reason,
                    priorities: item.priorities,
                },
                workstreamName,
            ),
        );
}

function buildRequests(workstreamName, apiQuestions = []) {
    const catalog = irlCatalogForWorkstream(workstreamName);
    const items = catalog.map(entry => toRequestItem(entry, workstreamName));
    (apiQuestions || []).forEach(question => {
        const request = (question.question || '').trim();
        if (!request) return;
        if (items.some(item => item.request.toLowerCase() === request.toLowerCase())) return;
        items.push(
            toRequestItem(
                {
                    folder: question.tag || 'General',
                    request,
                    selected: true,
                },
                workstreamName,
            ),
        );
    });
    return items;
}

function groupByFolder(items) {
    const groups = [];
    const indexByFolder = new Map();
    items.forEach(item => {
        if (!indexByFolder.has(item.folder)) {
            indexByFolder.set(item.folder, groups.length);
            groups.push({ folder: item.folder, items: [] });
        }
        groups[indexByFolder.get(item.folder)].items.push(item);
    });
    return groups;
}

const SetupIrl = ({
    project,
    workstreams = [],
    irlSuggestion = null,
    onContinue,
    onExit,
    documentBanner,
    onDismissDocumentBanner,
    onRefreshRecommendations,
    refreshingRecommendations = false,
    onRefreshSettled,
    refreshToken = 0,
}) => {
    const dispatch = useDispatch();
    const [requestsByWs, setRequestsByWs] = React.useState({});
    const [foldersByWs, setFoldersByWs] = React.useState({});
    const [activeWsId, setActiveWsId] = React.useState(workstreams[0]?.id || null);
    const [ready, setReady] = React.useState(false);
    const [editingKey, setEditingKey] = React.useState(null);
    const [draftRequest, setDraftRequest] = React.useState('');
    const [whyKey, setWhyKey] = React.useState(null);
    const [addingCustom, setAddingCustom] = React.useState(false);
    const [customFolder, setCustomFolder] = React.useState('');
    const [customRequest, setCustomRequest] = React.useState('');
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        if (!workstreams.length) {
            setReady(true);
            onRefreshSettled?.();
            return undefined;
        }
        let cancelled = false;
        setReady(false);
        const load = async () => {
            const folderResults = await Promise.all(
                workstreams.map(async ws => {
                    const foldersRes = await dispatch(GetDataroomItemInfo(ws.id));
                    const folders = (foldersRes?.payload?.data || []).map(folder => ({
                        id: folder.id,
                        name: folder.name,
                    }));
                    return { id: ws.id, name: ws.name, folders };
                }),
            );
            if (cancelled) return;
            let suggestion = refreshToken ? null : irlSuggestion;
            if (!suggestion) {
                try {
                    suggestion = await suggestIrl(
                        project?.id,
                        folderResults.map(item => ({
                            id: item.id,
                            name: item.name,
                            folders: item.folders,
                        })),
                    );
                } catch {
                    suggestion = null;
                }
            }
            const results = await Promise.all(
                workstreams.map(async ws => {
                    const folderNames = (folderResults.find(item => item.id === ws.id)?.folders || [])
                        .map(folder => folder.name)
                        .filter(Boolean);
                    const suggested = buildRequestsFromSuggestion(suggestion?.requests || [], ws.id, ws.name);
                    if (suggested.length) {
                        return { id: ws.id, requests: suggested, folders: folderNames };
                    }
                    const questionsRes = await dispatch(GetDefaultQuestions(ws.name));
                    const apiQuestions = questionsRes?.payload?.data || [];
                    return {
                        id: ws.id,
                        requests: buildRequests(ws.name, apiQuestions),
                        folders: folderNames,
                    };
                }),
            );
            if (cancelled) return;
            setRequestsByWs(Object.fromEntries(results.map(item => [item.id, item.requests])));
            setFoldersByWs(Object.fromEntries(results.map(item => [item.id, item.folders])));
            setActiveWsId(prev => prev || workstreams[0]?.id || null);
            setReady(true);
            onRefreshSettled?.();
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [dispatch, irlSuggestion, project?.id, refreshToken, workstreams]);

    const activeWorkstream = workstreams.find(ws => ws.id === activeWsId) || workstreams[0];
    const requests = (activeWorkstream && requestsByWs[activeWorkstream.id]) || [];
    const folderOptions = Array.from(
        new Set([...(foldersByWs[activeWorkstream?.id] || []), ...requests.map(item => item.folder)].filter(Boolean)),
    );

    const updateRequests = (wsId, updater) => {
        setRequestsByWs(prev => ({
            ...prev,
            [wsId]: updater(prev[wsId] || []),
        }));
    };

    const startEdit = item => {
        setAddingCustom(false);
        setWhyKey(null);
        setEditingKey(item.key);
        setDraftRequest(item.request);
    };

    const cancelEdit = () => {
        setEditingKey(null);
        setDraftRequest('');
    };

    const saveEdit = () => {
        if (!activeWorkstream) return;
        const request = draftRequest.trim();
        if (!request) return;
        updateRequests(activeWorkstream.id, list => list.map(item => (item.key === editingKey ? { ...item, request } : item)));
        cancelEdit();
    };

    const addCustom = () => {
        if (!activeWorkstream) return;
        const folder = customFolder.trim();
        const request = customRequest.trim();
        if (!folder || !request) return;
        updateRequests(activeWorkstream.id, list => [
            ...list,
            toRequestItem({ folder, request, selected: true, custom: true }, activeWorkstream.name),
        ]);
        setCustomFolder('');
        setCustomRequest('');
        setAddingCustom(false);
    };

    const selectedCount = () =>
        workstreams.reduce(
            (sum, ws) =>
                sum + (requestsByWs[ws.id] || []).filter(item => item.selected && item.request.trim()).length,
            0,
        );

    const persist = async () => {
        const items = [];
        workstreams.forEach(ws => {
            (requestsByWs[ws.id] || []).forEach(item => {
                if (!item.selected || !item.request.trim()) return;
                items.push({
                    request: item.request.trim(),
                    vdr: project?.vdr?.id,
                    tag: String(item.folder || 'General').slice(0, 50),
                    workstream: ws.id,
                    priorities: item.high ? 'HIGH' : item.priorities || 'NONE',
                    request_class: 'Custom',
                    added_by_offline: 'Project Admin',
                });
            });
        });
        if (!items.length) return true;
        const result = await dispatch(AddBulkInformationRequests({ items }));
        return result?.type === 'POST_INFORMATION_REQUESTS_BULK_ADD/fulfilled';
    };

    const handleCreate = async () => {
        if (saving) return;
        setSaving(true);
        const ok = await persist();
        setSaving(false);
        if (ok) onContinue(selectedCount());
    };

    const handleExit = async () => {
        if (saving) return;
        setSaving(true);
        const ok = await persist();
        setSaving(false);
        if (!ok) return;
        if (typeof onExit === 'function') onExit();
        else onContinue(selectedCount());
    };

    const continueDisabled = !ready || saving || Boolean(editingKey);
    const groups = groupByFolder(requests);

    return (
        <NewProjectWizardCard
            step={3}
            totalSteps={4}
            irlWide
            flushBody
            hideFooter
            title="Review initial information requests"
            subtitle="Lens has prepared an initial Information Request List based on the project context and confirmed VDR structure. Review it before creating the IRL."
            headerAside={<span className={styles.suggestedBadge}>Suggested by Lens</span>}
        >
            <div className={styles.vdrSplit}>
                <aside className={styles.vdrSidebar}>
                    <p className={styles.vdrSidebarLabel}>Workstreams</p>
                    {workstreams.map(ws => {
                        const list = requestsByWs[ws.id] || [];
                        const active = ws.id === activeWorkstream?.id;
                        return (
                            <button
                                key={ws.id}
                                type="button"
                                className={`${styles.vdrSidebarItem} ${active ? styles.vdrSidebarItemActive : ''}`}
                                onClick={() => {
                                    setActiveWsId(ws.id);
                                    cancelEdit();
                                    setAddingCustom(false);
                                    setWhyKey(null);
                                }}
                            >
                                <span className={styles.vdrSidebarName}>{ws.name}</span>
                                <span className={styles.vdrSidebarCount}>{list.length}</span>
                            </button>
                        );
                    })}
                </aside>

                <div className={styles.vdrMain}>
                    {documentBanner ? (
                        <DocumentContextBanner
                            variant={documentBanner}
                            onDismiss={onDismissDocumentBanner}
                            onRefresh={onRefreshRecommendations}
                            refreshing={refreshingRecommendations}
                            onNotNow={onDismissDocumentBanner}
                        />
                    ) : null}
                    <div className={styles.vdrMainHeader}>
                        <div>
                            <h2 className={styles.vdrMainTitle}>{activeWorkstream?.name || 'Workstream'}</h2>
                            <p className={styles.vdrMainCount}>
                                {requests.filter(item => item.selected).length} of {requests.length} selected
                            </p>
                        </div>
                        <div className={styles.irlHeaderLinks}>
                            <button
                                type="button"
                                className={styles.vdrSelectAll}
                                onClick={() => updateRequests(activeWorkstream.id, list => list.map(item => ({ ...item, selected: true })))}
                            >
                                Select all
                            </button>
                            <span className={styles.irlHeaderDot}>·</span>
                            <button
                                type="button"
                                className={styles.vdrSelectAll}
                                onClick={() => updateRequests(activeWorkstream.id, list => list.map(item => ({ ...item, selected: false })))}
                            >
                                Deselect all
                            </button>
                        </div>
                    </div>

                    <div className={styles.vdrList}>
                        {!ready ? <SetupSuggestionLoader label="Loading recommended requests…" /> : null}
                        {ready && requests.length === 0 && !addingCustom ? (
                            <p className={styles.vdrEmpty}>No recommended requests yet. Add an information request to get started.</p>
                        ) : null}
                        {groups.map(group => (
                            <section key={group.folder} className={styles.irlGroup}>
                                <h3 className={styles.irlGroupLabel}>{group.folder}</h3>
                                <div className={styles.irlGroupList}>
                                    {group.items.map(item => (
                                        <RequestRow
                                            key={item.key}
                                            item={item}
                                            editing={editingKey === item.key}
                                            whyOpen={whyKey === item.key}
                                            draftRequest={draftRequest}
                                            onDraftRequest={setDraftRequest}
                                            onToggle={() =>
                                                updateRequests(activeWorkstream.id, list =>
                                                    list.map(entry => (entry.key === item.key ? { ...entry, selected: !entry.selected } : entry)),
                                                )
                                            }
                                            onEdit={() => startEdit(item)}
                                            onSave={saveEdit}
                                            onCancel={cancelEdit}
                                            onToggleWhy={() => setWhyKey(prev => (prev === item.key ? null : item.key))}
                                        />
                                    ))}
                                </div>
                            </section>
                        ))}

                        {addingCustom ? (
                            <div className={styles.vdrCustomForm}>
                                <select
                                    className={styles.irlFolderSelect}
                                    value={customFolder}
                                    onChange={e => setCustomFolder(e.target.value)}
                                >
                                    <option value="">Select folder / category</option>
                                    {folderOptions.map(folder => (
                                        <option key={folder} value={folder}>
                                            {folder}
                                        </option>
                                    ))}
                                </select>
                                <Textarea
                                    className={`${styles.textareaInput} ${styles.wsDescriptionInput}`}
                                    value={customRequest}
                                    onChange={e => setCustomRequest(e.target.value)}
                                    placeholder="Information request text"
                                    rows={2}
                                    resize="none"
                                />
                                <div className={styles.wsEditActions}>
                                    <button
                                        type="button"
                                        className={styles.primaryButton}
                                        disabled={!customFolder.trim() || !customRequest.trim()}
                                        onClick={addCustom}
                                    >
                                        Add request
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.ghostButton}
                                        onClick={() => {
                                            setAddingCustom(false);
                                            setCustomFolder('');
                                            setCustomRequest('');
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className={styles.addScopeLink}
                                onClick={() => {
                                    cancelEdit();
                                    setWhyKey(null);
                                    setAddingCustom(true);
                                }}
                            >
                                + Add information request
                            </button>
                        )}
                    </div>

                    <div className={styles.vdrFooter}>
                        <button
                            type="button"
                            className={`${styles.primaryButton} ${saving ? styles.primaryButtonLoading : ''}`}
                            disabled={continueDisabled}
                            aria-busy={saving || undefined}
                            onClick={handleCreate}>
                            Create initial IRL
                            {saving ? <span className={styles.buttonSpinner} aria-hidden="true" /> : null}
                        </button>
                        <button type="button" className={styles.ghostButton} disabled={saving} onClick={handleExit}>
                            Save & exit
                        </button>
                    </div>
                </div>
            </div>
        </NewProjectWizardCard>
    );
};

const RequestRow = ({
    item,
    editing,
    whyOpen,
    draftRequest,
    onDraftRequest,
    onToggle,
    onEdit,
    onSave,
    onCancel,
    onToggleWhy,
}) => {
    if (editing) {
        return (
            <div className={`${styles.irlRow} ${styles.irlRowSelected}`}>
                <div className={styles.wsEditFields}>
                    <Textarea
                        className={`${styles.textareaInput} ${styles.wsDescriptionInput}`}
                        value={draftRequest}
                        onChange={e => onDraftRequest(e.target.value)}
                        rows={2}
                        resize="none"
                    />
                    <div className={styles.wsEditActions}>
                        <button type="button" className={styles.primaryButton} disabled={!draftRequest.trim()} onClick={onSave}>
                            Save
                        </button>
                        <button type="button" className={styles.ghostButton} onClick={onCancel}>
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const selected = item.selected;

    return (
        <div className={`${styles.irlRow} ${selected ? styles.irlRowSelected : styles.irlRowIdle}`}>
            <button
                type="button"
                role="checkbox"
                aria-checked={selected}
                className={`${styles.wsCheckbox} ${selected ? styles.wsCheckboxChecked : ''}`}
                onClick={onToggle}
            >
                {selected ? <img src={checkboxCheck} alt="" width={11} height={11} className={styles.wsCheckboxCheck} /> : null}
            </button>
            <div className={styles.irlRowBody}>
                <div className={styles.irlRowTop}>
                    <p className={styles.irlRequest}>{item.request}</p>
                    <div className={styles.irlRowActions}>
                        {item.high ? <span className={styles.irlHigh}>High</span> : null}
                        <button type="button" className={styles.vdrTextLink} onClick={onEdit}>
                            Edit
                        </button>
                        <button type="button" className={styles.vdrTextLink} onClick={onToggleWhy}>
                            Why?
                        </button>
                    </div>
                </div>
                {whyOpen ? <p className={styles.irlWhy}>{item.why}</p> : null}
            </div>
        </div>
    );
};

export default SetupIrl;
